import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaDownload } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import '../styles/EmployeeAttendanceDetails.css';
import AttendanceStrategyFactory from '../services/attendance/AttendanceStrategyFactory';

function EmployeeAttendanceDetails() {
     const navigate = useNavigate();
     const token = sessionStorage.getItem("token");
     const BASE_URL = process.env.REACT_APP_BASE_URL;

     // Get employee code from session - FIXED: using correct session storage keys
     const sessionEmployeeCode = sessionStorage.getItem("user_code");
     const sessionEmployeeName = sessionStorage.getItem("user_name");

     const [selectedEmployee] = useState(sessionEmployeeCode || "");
     const [selectedEmployeeName] = useState(sessionEmployeeName || "");
     const [employeeType, setEmployeeType] = useState("");
     const [selectedMonth, setSelectedMonth] = useState("");
     const [selectedToMonth, setSelectedToMonth] = useState("");

     const [attendance, setAttendance] = useState({ days: [] });
     const [loading, setLoading] = useState(false);
     const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
     const [attendanceStrategy, setAttendanceStrategy] = useState(null);

     const days = Array.isArray(attendance.days) ? attendance.days : [];

     useEffect(() => {
          const handleResize = () => {
               setIsMobile(window.innerWidth <= 768);
          };
          window.addEventListener('resize', handleResize);
          return () => window.removeEventListener('resize', handleResize);
     }, []);

     useEffect(() => {
          // Set current month as default
          const now = new Date();
          const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
          setSelectedMonth(currentMonth);
          setSelectedToMonth(currentMonth);
     }, []);



     const fetchAttendance = React.useCallback(async () => {
          if (!selectedEmployee) {
               console.error('No employee code found in session');
               return;
          }

          try {
               setLoading(true);
               const url = `${BASE_URL}/getEmployeeAttendance`;
               const payload = {
                    employee_code: selectedEmployee,
                    selectedMonth: selectedMonth,
                    selectedToMonth: selectedToMonth
               };

               const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                         'Content-Type': 'application/json',
                         'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload)
               });

               if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
               }

               const json = await response.json();

               if (json.status && json.data) {
                    // Build regularize map from top-level regularize array
                    const regList = Array.isArray(json.regularize) ? json.regularize : [];
                    const regByDate = {};
                    regList.forEach(r => {
                         const d = r.for_date || r.forDate || r.date || null;
                         if (d) {
                              regByDate[d] = regByDate[d] || [];
                              regByDate[d].push(r);
                         }
                    });

                    // Attach regularize entries to days
                    const daysArr = Array.isArray(json.data.days) ? json.data.days : [];
                    const enrichedDays = daysArr.map(d => ({ ...d, regularize: regByDate[d.date] || [] }));

                    // Add any regularize-only dates so details view will show them too
                    const regOnlyDates = Object.keys(regByDate).filter(d => !daysArr.some(x => x.date === d));
                    const regOnlyDays = regOnlyDates.map(d => ({
                         date: d,
                         punch_in: null,
                         punch_out: null,
                         all_punches: [],
                         shift_total_minutes: 0,
                         worked_seconds: 0,
                         shortfall_seconds: 0,
                         shortfall_hours: '00:00:00',
                         has_shortfall: false,
                         regularize: regByDate[d]
                    }));

                    const mergedDays = [...enrichedDays, ...regOnlyDays].sort((a, b) => a.date.localeCompare(b.date));

                    const attendanceData = {
                         ...json.data,
                         days: mergedDays,
                         regularize: regList
                    };

                    setAttendance(attendanceData);

                    const empType = json.data.employee_info?.emp_type || 'CONTRACTUAL_EMPLOYEE';
                    setEmployeeType(empType);

                    const splitFlag = json.data.employee_info?.split_shift || 'N';
                    const strategy = AttendanceStrategyFactory.getStrategy(empType, splitFlag);
                    setAttendanceStrategy(strategy);
               } else {
                    setAttendance({ days: [] });
               }
          } catch (err) {
               console.error('Error fetching attendance:', err);
               setAttendance({ days: [] });
          } finally {
               setLoading(false);
          }
     }, [BASE_URL, selectedEmployee, selectedMonth, selectedToMonth, token]);

     useEffect(() => {
          if (selectedEmployee && selectedMonth && selectedToMonth) {
               fetchAttendance();
          }
     }, [selectedMonth, selectedToMonth, selectedEmployee, fetchAttendance]);

     useEffect(() => {
          const splitFlag = attendance.employee_info?.split_shift || 'N';
          if (!employeeType) return;
          try {
               const strategy = AttendanceStrategyFactory.getStrategy(employeeType, splitFlag);
               setAttendanceStrategy(strategy);
          } catch {
               setAttendanceStrategy(AttendanceStrategyFactory.getStrategy('Contractual_Employee', 'N'));
          }
     }, [employeeType, attendance.employee_info?.split_shift]);

     const handleFetchAttendance = () => {
          if (selectedEmployee && selectedMonth && selectedToMonth) {
               fetchAttendance();
          }
     };

     const calculateAttendanceStatus = (day, roster, holidays) => {
          if (!attendanceStrategy) {
               return { status: "-", lateMark: "-", workingHours: "-", statusColor: "#94a3b8", remarks: "" };
          }

          const empData = {
               weekAttendance: attendance.weekAttendance || [],
               allDays: attendance.days || [],
               region: attendance.employee_region || 'Other'
          };

          const result = attendanceStrategy.calculateAttendanceStatus(day, roster, holidays, empData);

          // Format working hours if present
          if (result.workingHours && result.workingHours !== '-') {
               result.workingHours = (String(result.workingHours).indexOf(':') === -1) ? result.workingHours : result.workingHours;
          }

          // Apply regularize override similar to list/calendar views
          try {
               const approvedRegs = Array.isArray(day.regularize) ? day.regularize.filter(r => (r.status || '').toLowerCase() === 'approved') : [];
               if (approvedRegs.length > 0) {
                    let totalRegSeconds = 0;
                    approvedRegs.forEach(r => {
                         const pin = r.punch_in || r.punchIn || null;
                         const pout = r.punch_out || r.punchOut || null;
                         if (pin && pout) {
                              let inDate = pin.includes('-') || pin.includes('T') ? new Date(pin) : new Date(day.date + ' ' + pin);
                              let outDate = pout.includes('-') || pout.includes('T') ? new Date(pout) : new Date(day.date + ' ' + pout);
                              if (inDate && outDate && outDate > inDate) {
                                   totalRegSeconds += Math.floor((outDate - inDate) / 1000);
                              }
                         }
                    });

                    if (totalRegSeconds > 0) {
                         const shiftHours = parseFloat(roster?.total_hours) || 8;
                         const halfThresholdHours = parseFloat(roster?.WorkingsHoursToBeConsiderdHalfDay) || (shiftHours / 2);

                         const shiftSeconds = Math.floor(shiftHours * 3600);
                         const halfSeconds = Math.floor(halfThresholdHours * 3600);

                         // compute reg late mark
                         let regLateMark = '-';
                         try {
                              const regIns = approvedRegs.map(r => r.punch_in || r.punchIn).filter(Boolean);
                              if (regIns.length && roster && roster.in_time) {
                                   const earliestRegIn = regIns.map(pin => (pin.includes('-') || pin.includes('T')) ? new Date(pin) : new Date(day.date + ' ' + pin)).reduce((a, b) => a < b ? a : b);
                                   const rosterIn = roster.in_time ? new Date(day.date + ' ' + roster.in_time) : null;
                                   let allowedIn = rosterIn;
                                   if (roster && roster.grace_in) {
                                        allowedIn = new Date(allowedIn.getTime() + (parseFloat(roster.grace_in || 0) * 60 * 1000));
                                   }
                                   if (allowedIn && earliestRegIn && earliestRegIn > allowedIn) regLateMark = 'Late';
                              }
                         } catch (e) {
                              console.warn('Failed to compute reg late mark', e);
                              regLateMark = '-';
                         }

                         const regWorking = secondsToHMS(totalRegSeconds);

                         if (totalRegSeconds >= shiftSeconds) {
                              return {
                                   status: 'Present',
                                   lateMark: result.lateMark || '-',
                                   workingHours: regWorking,
                                   statusColor: '#10b981',
                                   remarks: 'Present via regularize',
                                   regWorkingHours: regWorking,
                                   regLateMark: regLateMark
                              };
                         } else if (totalRegSeconds >= halfSeconds) {
                              return {
                                   status: 'Half Day',
                                   lateMark: result.lateMark || '-',
                                   workingHours: regWorking,
                                   statusColor: '#f59e0b',
                                   remarks: 'Half day via regularize',
                                   regWorkingHours: regWorking,
                                   regLateMark: regLateMark
                              };
                         } else {
                              return {
                                   status: 'LOP',
                                   lateMark: result.lateMark || '-',
                                   workingHours: regWorking,
                                   statusColor: '#ef4444',
                                   remarks: 'LOP (regularize insufficient)',
                                   regWorkingHours: regWorking,
                                   regLateMark: regLateMark
                              };
                         }
                    }
               }
          } catch (e) {
               console.error('Error processing regularize override in details:', e);
          }

          const now = new Date();
          const todayString = [
               now.getFullYear(),
               String(now.getMonth() + 1).padStart(2, '0'),
               String(now.getDate()).padStart(2, '0')
          ].join('-');

          const dayDateNormalized = String(day.date).trim();
          const todayNormalized = todayString.trim();
          const isToday = dayDateNormalized === todayNormalized;
          const hasPunchIn = Boolean(day.punch_in);
          const hasPunchOut = Boolean(day.punch_out);
          const currentHour = now.getHours();
          const isDayStillOngoing = currentHour < 23;

          if (isToday && hasPunchIn && result.status === "LOP" && isDayStillOngoing) {
               return {
                    status: "OnDuty",
                    lateMark: result.lateMark || "-",
                    workingHours: hasPunchOut ? result.workingHours : "-",
                    statusColor: "#3b82f6",
                    remarks: "Currently on duty"
               };
          }

          return result;
     };

     const calculateShortfallSummary = () => {
          let totalShortfallSeconds = 0;
          let totalWorkedSeconds = 0;
          let totalPresentDays = 0;
          let totalWeekendDays = 0;
          let exemptionLimit = "00:00:00";

          days.forEach(day => {
               const dateObj = new Date(day.date);
               const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
               const { status } = calculateAttendanceStatus(day, roster, attendance.holidays);

               if (roster && roster.exemption_limit && exemptionLimit === "00:00:00") {
                    const exemptionHours = parseFloat(roster.exemption_limit);
                    const hours = Math.floor(exemptionHours);
                    const minutes = Math.round((exemptionHours - hours) * 60);
                    exemptionLimit = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
               }

               const isSunday = dateObj.getDay() === 0;
               const isWeekOff = status === "Week Off" || status === "Weekoff";
               if (isSunday || isWeekOff) {
                    totalWeekendDays++;
               }

               if (day.has_shortfall) {
                    totalShortfallSeconds += day.shortfall_seconds || 0;
               }

               if (day.punch_in && day.punch_out) {
                    totalPresentDays++;
                    totalWorkedSeconds += day.worked_seconds || 0;
               }
          });

          const secondsToHMS = (totalSeconds) => {
               const hours = Math.floor(totalSeconds / 3600);
               const minutes = Math.floor((totalSeconds % 3600) / 60);
               const seconds = totalSeconds % 60;
               return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
          };

          return {
               shortfallDays: days.filter(day => day.has_shortfall).length,
               totalShortfall: secondsToHMS(totalShortfallSeconds),
               totalWorkedHours: secondsToHMS(totalWorkedSeconds),
               totalPresentDays: totalPresentDays,
               totalWeekendDays: totalWeekendDays,
               exemptionLimit: exemptionLimit
          };
     };

     const shortfallSummary = calculateShortfallSummary();

     const formatAllPunches = (allPunches) => {
          if (!allPunches || allPunches.length === 0) return '-';
          return allPunches.map(punch => {
               return new Date(punch).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
               });
          }).join(', ');
     };

     const formatDate = (dateString) => {
          const dateObj = new Date(dateString);
          const dd = String(dateObj.getDate()).padStart(2, "0");
          const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
          const yyyy = dateObj.getFullYear();
          return `${dd}-${mm}-${yyyy}`;
     };

     const formatTime = (timeString) => {
          if (!timeString) return '-';
          if (timeString.includes('T') || timeString.includes(' ')) {
               return new Date(timeString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
          }
          if (timeString.match(/^\d{2}:\d{2}:\d{2}$/)) {
               return timeString;
          }
          return timeString;
     };

     // helper to format seconds to HH:MM
     const secondsToHMS = (totalSeconds) => {
          if (!totalSeconds) return "-";
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
     };

     const formatTimeForExcel = (timeString) => {
          if (!timeString) return "-";
          if (timeString.includes('T') || timeString.includes(' ')) {
               return new Date(timeString).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
               });
          }
          return timeString;
     };


     const handleDownloadExcel = () => {
          if (!days.length) return;

          const data = days.map(day => {
               const dateObj = new Date(day.date);
               const formattedDate = formatDate(day.date);
               const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
               const { status, workingHours, remarks } = calculateAttendanceStatus(day, roster, attendance.holidays);

               const shift1 = (day.shifts || []).find(s => s.split_shift_type === 'shift_1') || {};
               const shift2 = (day.shifts || []).find(s => s.split_shift_type === 'shift_2') || {};

               // Regularize summary for export
               const regList = Array.isArray(day.regularize) ? day.regularize : [];
               const regCount = regList.length;
               const regDetails = regList.map(r => {
                    const approver = r.approved_by_name || r.approver_name || r.approved_by || r.approver || r.approverName || '';
                    const attCount = Array.isArray(r.attachments) ? r.attachments.length : (Array.isArray(r.attachment_ids) ? r.attachment_ids.length : 0);
                    const inT = (r.punch_in || r.punchIn) ? (r.punch_in || r.punchIn) : '-';
                    const outT = (r.punch_out || r.punchOut) ? (r.punch_out || r.punchOut) : '-';
                    const reason = r.reason || '';
                    return `${inT} / ${outT} (${r.status || ''}${approver ? ' - By: ' + approver : ''}${attCount ? ' - Attachments: ' + attCount : ''}${reason ? ' - ' + reason : ''})`;
               }).join(' | ');

               // Sum approved regularize durations (in seconds) for Reg Hrs
               const regSeconds = regList.reduce((acc, r) => {
                    try {
                         if ((r.status || '').toLowerCase() !== 'approved') return acc;
                         const inStr = r.punch_in || r.punchIn;
                         const outStr = r.punch_out || r.punchOut;
                         if (!inStr || !outStr) return acc;
                         const [ih, im, is] = inStr.split(':').map(x => Number(x));
                         const [oh, om, os] = outStr.split(':').map(x => Number(x));
                         const startSec = ih * 3600 + im * 60 + is;
                         const endSec = oh * 3600 + om * 60 + os;
                         if (endSec >= startSec) return acc + (endSec - startSec);
                    } catch (e) {
                         return acc;
                    }
                    return acc;
               }, 0);
               const regHrsStr = regSeconds ? secondsToHMS(regSeconds) : "-";

               return {
                    "Employee Code": selectedEmployee,
                    "Employee Name": selectedEmployeeName,
                    "Employee Type": employeeType,
                    "Date": formattedDate,
                    "Day": ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][dateObj.getDay()],
                    "Punch In": formatTimeForExcel(day.punch_in),
                    "Punch Out": formatTimeForExcel(day.punch_out),
                    "S1 In": shift1.punch_in ? formatTimeForExcel(shift1.punch_in) : "-",
                    "S1 Out": shift1.punch_out ? formatTimeForExcel(shift1.punch_out) : "-",
                    "S2 In": shift2.punch_in ? formatTimeForExcel(shift2.punch_in) : "-",
                    "S2 Out": shift2.punch_out ? formatTimeForExcel(shift2.punch_out) : "-",
                    "All Punches": formatAllPunches(day.all_punches || ([]).concat(shift1.all_punches || [], shift2.all_punches || [])),
                    "Working Hours": workingHours || "-",
                    "Shift Name": roster?.shift_name || "-",
                    "Shift In Time": roster?.in_time || "-",
                    "Shift Out Time": roster?.out_time || "-",
                    "Status": status,
                    "Regularized": regCount ? 'Yes' : 'No',
                    "Reg Count": regCount,
                    "Reg Details": regDetails,
                    "Reg Hrs": regHrsStr,
                    "Remarks": remarks || ""
               };
          });

          const summaryData = [{
               "Employee Code": selectedEmployee,
               "Employee Name": selectedEmployeeName,
               "Employee Type": employeeType,
               "Month Range": `${attendance.month_range?.start_month_name} ${attendance.month_range?.start_year}`,
               "Total Days": attendance.attendance?.total_days || 0,
               "Present Days": attendance.attendance?.present_days || 0,
               "Absent Days": attendance.attendance?.absent_days || 0,
               "Total Worked Hours": shortfallSummary.totalWorkedHours,
               "Weekend Days": shortfallSummary.totalWeekendDays
          }];

          const wb = XLSX.utils.book_new();
          const summaryWs = XLSX.utils.json_to_sheet(summaryData);
          XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");
          const attendanceWs = XLSX.utils.json_to_sheet(data);
          XLSX.utils.book_append_sheet(wb, attendanceWs, "Detailed Attendance");

          const filename = `${selectedEmployeeName}_${employeeType}_Attendance_${attendance.month_range?.start}_to_${attendance.month_range?.end}.xlsx`;
          XLSX.writeFile(wb, filename);
     };

     // New: doctor split-shift desktop table
     const renderDoctorSplitDesktopTable = () => (
          <div className="table-responsive">
               <table className="attendance-table">
                    <thead>
                         <tr>
                              <th>DATE</th>
                              <th>DAY</th>
                              <th>SHIFT 1</th>
                              <th>S1 IN/OUT</th>
                              <th>SHIFT 2</th>
                              <th>S2 IN/OUT</th>
                              <th>TOTAL WORKED</th>
                              <th>STATUS</th>
                              <th>ALL PUNCHES</th>
                         </tr>
                    </thead>
                    <tbody>
                         {days.map(day => {
                              const dateObj = new Date(day.date);
                              const formattedDate = formatDate(day.date);
                              const shift1 = (day.shifts || []).find(s => s.split_shift_type === 'shift_1') || {};
                              const shift2 = (day.shifts || []).find(s => s.split_shift_type === 'shift_2') || {};
                              const s1In = shift1.punch_in ? formatTime(shift1.punch_in) : "-";
                              const s1Out = shift1.punch_out ? formatTime(shift1.punch_out) : "-";
                              const s2In = shift2.punch_in ? formatTime(shift2.punch_in) : "-";
                              const s2Out = shift2.punch_out ? formatTime(shift2.punch_out) : "-";
                              const totalWorked = secondsToHMS((shift1.worked_seconds || 0) + (shift2.worked_seconds || 0));
                              const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
                              const { status, statusColor, regWorkingHours } = calculateAttendanceStatus(day, roster, attendance.holidays);

                              return (
                                   <tr key={day.date}>
                                        <td>{formattedDate}</td>
                                        <td>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dateObj.getDay()]}</td>
                                        <td>{shift1.shift_name || "-"}</td>
                                        <td style={{ color: shift1.punch_in || shift1.punch_out ? '#10b981' : '#6b7280' }}>{s1In} / {s1Out}</td>
                                        <td>{shift2.shift_name || "-"}</td>
                                        <td style={{ color: shift2.punch_in || shift2.punch_out ? '#10b981' : '#6b7280' }}>{s2In} / {s2Out}</td>
                                        <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                             {totalWorked}
                                             {regWorkingHours && (
                                                  <div style={{ fontSize: '0.85rem', color: '#059669', marginTop: 4 }}>Reg Hrs: {regWorkingHours}</div>
                                             )}
                                        </td>
                                        <td><span className="status-badge" style={{ backgroundColor: statusColor }}>{status}</span></td>
                                        <td style={{ fontSize: '0.85rem', color: '#475569' }}>{formatAllPunches([...(shift1.all_punches || []), ...(shift2.all_punches || [])])}</td>
                                   </tr>
                              );
                         })}
                    </tbody>
               </table>
          </div>
     );

     // New: doctor split-shift mobile cards
     const renderDoctorSplitMobileCards = () => (
          <div className="mobile-cards-container">
               {days.map((day, index) => {
                    const dateObj = new Date(day.date);
                    const formattedDate = formatDate(day.date);
                    const shift1 = (day.shifts || []).find(s => s.split_shift_type === 'shift_1') || {};
                    const shift2 = (day.shifts || []).find(s => s.split_shift_type === 'shift_2') || {};
                    const totalWorked = secondsToHMS((shift1.worked_seconds || 0) + (shift2.worked_seconds || 0));
                    const rosterForDay = attendance.duty_roster?.filter(r => r.attendance_date === day.date) || [];
                    const { status, statusColor, regWorkingHours } = calculateAttendanceStatus(day, rosterForDay[0], attendance.holidays);
                    const isWeekOff = status === "Week Off" || status === "Weekoff";

                    return (
                         <div key={day.date || index} className="mobile-card-item" style={{
                              background: isWeekOff ? '#eff6ff' : '#ffffff',
                              border: isWeekOff ? '2px solid #bfdbfe' : '2px solid #e2e8f0'
                         }}>
                              <div className="mobile-card-header" style={{
                                   background: isWeekOff ? '#dbeafe' : '#f8fafc',
                                   borderBottom: isWeekOff ? '2px solid #bfdbfe' : '2px solid #e2e8f0'
                              }}>
                                   <div className="mobile-card-date-info">
                                        <span className="mobile-card-date">{formattedDate}</span>
                                        <span className="mobile-card-day">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dateObj.getDay()]}</span>
                                   </div>
                                   <span className="mobile-card-status" style={{ background: statusColor }}>{status}</span>
                              </div>

                              <div className="mobile-card-body" style={{ background: isWeekOff ? '#eff6ff' : '#ffffff' }}>
                                   <div className="mobile-card-row">
                                        <span className="mobile-card-label">S1</span>
                                        <span className="mobile-card-value">{shift1.shift_name || '-'}</span>
                                   </div>
                                   <div className="mobile-card-row">
                                        <span className="mobile-card-label">S1 IN/OUT</span>
                                        <span className="mobile-card-value">{shift1.punch_in ? formatTime(shift1.punch_in) : '-'} / {shift1.punch_out ? formatTime(shift1.punch_out) : '-'}</span>
                                   </div>
                                   <div style={{ height: 8 }} />
                                   <div className="mobile-card-row">
                                        <span className="mobile-card-label">S2</span>
                                        <span className="mobile-card-value">{shift2.shift_name || '-'}</span>
                                   </div>
                                   <div className="mobile-card-row">
                                        <span className="mobile-card-label">S2 IN/OUT</span>
                                        <span className="mobile-card-value">{shift2.punch_in ? formatTime(shift2.punch_in) : '-'} / {shift2.punch_out ? formatTime(shift2.punch_out) : '-'}</span>
                                   </div>
                                   <div className="mobile-card-row">
                                        <span className="mobile-card-label">TOTAL WORKED</span>
                                        <span className="mobile-card-value">{totalWorked}</span>
                                        {regWorkingHours && (
                                             <div style={{ fontSize: 12, color: '#059669', marginTop: 4 }}>Reg Hrs: {regWorkingHours}</div>
                                        )}
                                   </div>
                                   <div className="mobile-card-row">
                                        <span className="mobile-card-label">ALL PUNCHES</span>
                                        <span className="mobile-card-value">{formatAllPunches([...(shift1.all_punches || []), ...(shift2.all_punches || [])])}</span>
                                   </div>
                              </div>
                         </div>
                    );
               })}
          </div >
     );

     // Decide whether to use the doctor-split views
     const isDoctorSplit = (employeeType === "DOCTOR") && attendance.employee_info?.split_shift === "Y";




     const renderDesktopTable = () => (
          <div className="table-responsive">
               <table className="attendance-details-table">
                    <thead>
                         <tr>
                              <th>DATE</th>
                              <th>DAY</th>
                              <th>IN</th>
                              <th>OUT</th>

                              <th>HRS WORKED</th>
                              <th>SHIFT HRS</th>
                              <th>SHIFT</th>
                              <th>STATUS</th>
                              <th>ALL PUNCHES</th>
                         </tr>
                    </thead>
                    <tbody>
                         {days.map(day => {
                              const dateObj = new Date(day.date);
                              const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
                              const shiftName = roster?.shift_name || "-";
                              const shiftIn = roster?.in_time || "-";
                              const shiftOut = roster?.out_time || "-";
                              const { status, workingHours, statusColor } = calculateAttendanceStatus(day, roster, attendance.holidays);
                              const isWeekoff = status === "Week Off" || status === "Weekoff";
                              const isSunday = dateObj.getDay() === 0;
                              const showWorkingHours = day.punch_in && day.punch_out ? workingHours : "-";
                              const shiftHours = roster?.total_hours || '-';

                              // Compute total regularize seconds and display string
                              const regSeconds = Array.isArray(day.regularize) ? day.regularize.reduce((acc, r) => {
                                   const pin = r.punch_in || r.punchIn;
                                   const pout = r.punch_out || r.punchOut;
                                   if (!pin || !pout) return acc;
                                   const inDate = (pin.includes('T') || pin.includes('-')) ? new Date(pin) : new Date(day.date + ' ' + pin);
                                   const outDate = (pout.includes('T') || pout.includes('-')) ? new Date(pout) : new Date(day.date + ' ' + pout);
                                   if (inDate && outDate && outDate > inDate) return acc + Math.floor((outDate - inDate) / 1000);
                                   return acc;
                              }, 0) : 0;
                              const regHrs = regSeconds ? secondsToHMS(regSeconds) : '-';

                              return (
                                   <tr
                                        key={day.date}
                                        className={isWeekoff || isSunday ? "weekoff-row" : ""}
                                   >
                                        <td>{formatDate(day.date)}</td>
                                        <td>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dateObj.getDay()]}</td>
                                        <td>
                                             <div>{formatTime(day.punch_in)}</div>
                                             {Array.isArray(day.regularize) && day.regularize.length > 0 && (
                                                  <div style={{ marginTop: 4 }}>
                                                       {day.regularize.map((r) => {
                                                            const approver = r.approved_by_name || r.approver_name || r.approved_by || r.approver || r.approverName || null;
                                                            const attCount = Array.isArray(r.attachments) ? r.attachments.length : (Array.isArray(r.attachment_ids) ? r.attachment_ids.length : 0);
                                                            return (
                                                                 <div key={r.id} style={{ fontSize: '0.85rem', color: '#059669', marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                      <button type="button" onClick={() => navigate(`/regularize-request?date=${encodeURIComponent(r.for_date || r.forDate || r.date)}&id=${r.id}&edit=1`)} className="btn btn-link p-0" style={{ color: '#059669', textDecoration: 'underline' }}>
                                                                           Reg In: {formatTime(r.punch_in || r.punchIn) || '-'}{r.punch_out || r.punchOut ? ` / Reg Out: ${formatTime(r.punch_out || r.punchOut)}` : ''}
                                                                      </button>
                                                                      <span className={(r.status || '').toLowerCase() === 'approved' ? 'badge bg-success' : 'badge bg-warning text-dark'} style={{ fontSize: 11, padding: '3px 6px' }}>{r.status || ''}</span>
                                                                      {approver && <span style={{ fontSize: 11, color: '#64748b' }}>By: {approver}</span>}
                                                                      {attCount > 0 && <span style={{ fontSize: 11, color: '#64748b' }}>📎{attCount}</span>}
                                                                 </div>
                                                            );
                                                       })}
                                                  </div>
                                             )}
                                        </td>
                                        <td>
                                             <div>{formatTime(day.punch_out)}</div>
                                        </td>

                                        <td className="working-hours-cell">
                                             <div>{showWorkingHours}</div>
                                             {Array.isArray(day.regularize) && day.regularize.length > 0 && day.regularize[0].punch_in && day.regularize[0].punch_out && (
                                                  <div style={{ fontSize: '0.85rem', color: '#059669', marginTop: 4 }}>Reg Hrs: {regHrs}</div>
                                             )}
                                        </td>
                                        <td className="shift-hours-cell">{shiftHours}</td>
                                        <td className="shift-cell">
                                             {shiftName !== "-" ? (
                                                  <>
                                                       {shiftName} | {shiftIn} - {shiftOut}
                                                  </>
                                             ) : "-"}
                                        </td>
                                        <td>
                                             <span className="status-badge" style={{ backgroundColor: statusColor }}>
                                                  {status}
                                             </span>
                                        </td>
                                        <td className="all-punches-cell">{formatAllPunches(day.all_punches)}</td>
                                   </tr>
                              );
                         })}
                    </tbody>
               </table>
          </div>
     );

     const renderMobileCards = () => {
          return (
               <div className="mobile-cards-container">
                    {days.map((day, index) => {
                         const dateObj = new Date(day.date);
                         const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
                         const shiftName = roster?.shift_name || "-";
                         const shiftIn = formatTime(roster?.in_time);
                         const shiftOut = formatTime(roster?.out_time);
                         const { status, workingHours, statusColor, regWorkingHours } = calculateAttendanceStatus(day, roster, attendance.holidays);

                         const isWeekoff = status === "Week Off" || status === "Weekoff";
                         const isSunday = dateObj.getDay() === 0;
                         const shiftHours = roster?.total_hours || '-';

                         return (
                              <div
                                   key={day.date || index}
                                   className="mobile-card-item compact"
                                   style={{
                                        background: (isWeekoff || isSunday) ? '#eff6ff' : '#fff',
                                        border: (isWeekoff || isSunday) ? '1px solid #93c5fd' : '1px solid #e2e8f0'
                                   }}
                              >
                                   <div className="mobile-card-header" style={{
                                        background: (isWeekoff || isSunday) ? '#dbeafe' : '#f8fafc'
                                   }}>
                                        <div>
                                             <span className="mobile-card-date">{formatDate(day.date)}</span>
                                             <span className="mobile-card-day" style={{
                                                  background: (isWeekoff || isSunday) ? '#93c5fd' : '#e2e8f0'
                                             }}>
                                                  {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"][dateObj.getDay()]}
                                             </span>
                                        </div>
                                        <span className="mobile-card-status" style={{ background: statusColor }}>
                                             {status}
                                        </span>
                                   </div>
                                   <div className="mobile-card-body compact">
                                        <div className="mobile-row-2col">
                                             <div className='row' style={{ alignItems: 'center', gap: 8 }}>
                                                  <span className="mobile-label col-auto">IN: </span>
                                                  <span className="mobile-value col-auto">{formatTime(day.punch_in)}</span>
                                                  {Array.isArray(day.regularize) && day.regularize.length > 0 && (
                                                       <div style={{ marginLeft: 8 }}>
                                                            {day.regularize.map(r => (
                                                                 <div key={r.id} style={{ fontSize: 12, color: '#059669' }}>
                                                                      <button type="button" onClick={() => navigate(`/regularize-request?date=${encodeURIComponent(r.for_date || r.forDate || r.date)}&id=${r.id}&edit=1`)} className="btn btn-link p-0" style={{ color: '#059669', textDecoration: 'underline' }}>
                                                                           Reg: {formatTime(r.punch_in || r.punchIn) || '-'}{r.punch_out || r.punchOut ? ` / ${formatTime(r.punch_out || r.punchOut)}` : ''}
                                                                      </button>
                                                                 </div>
                                                            ))}
                                                       </div>
                                                  )}
                                             </div>
                                             <div className='row'>
                                                  <span className="mobile-label col-auto">OUT: </span>
                                                  <span className="mobile-value col-auto">{formatTime(day.punch_out)}</span>
                                             </div>
                                        </div>
                                        <div className="mobile-row-2col">
                                             <div className='row'>
                                                  <span className="mobile-label col-auto">HRS WORKED: </span>
                                                  <span className="mobile-value col-auto">{workingHours}</span>
                                                  {regWorkingHours && (
                                                       <div style={{ fontSize: 12, color: '#059669', marginLeft: 8 }}>Reg Hrs: {regWorkingHours}</div>
                                                  )}
                                             </div>
                                             <div className='row'>
                                                  <span className="mobile-label col-auto">SHIFT HRS: </span>
                                                  <span className="mobile-value col-auto">{shiftHours}</span>
                                             </div>
                                        </div>
                                        <div className="mobile-row-1col  ">
                                             <div className='row'>
                                                  <span className="mobile-label col-auto">SHIFT: </span>
                                                  <span className="mobile-value col-auto">
                                                       {shiftName !== "-" ? `${shiftName} | ${shiftIn} - ${shiftOut}` : '-'}
                                                  </span>
                                             </div>
                                        </div>
                                        <div className="mobile-row-1col">
                                             <div className='row'>
                                                  <span className="mobile-label col-auto">ALL PUNCHES: </span>
                                                  <span className="mobile-value  col-auto">
                                                       {formatAllPunches(day.all_punches) || '-'}
                                                  </span>
                                             </div>
                                        </div>
                                   </div>
                              </div>
                         );
                    })}
               </div>
          );
     };

     if (loading) {
          return (
               <div className="attendance-details-wrapper">
                    <div className="loading-message">Loading attendance data...</div>
               </div>
          );
     }

     if (!selectedEmployee) {
          return (
               <div className="attendance-details-wrapper">
                    <div className="no-data-message">
                         <p>No employee code found in session. Please login again.</p>
                    </div>
               </div>
          );
     }

     return (
          <div className="attendance-details-wrapper">


               {/* Filters Section - Only Month Selection */}
               <div className="filters-section row">
                    <div className="filter-group col-sm-4">

                         <b>{selectedEmployeeName}</b>
                         <small>{employeeType} | {selectedEmployee}</small>

                    </div>
                    <div className="filter-group col-auto">
                         <label>From Month</label>
                         <input
                              className='form-control form-control-sm'
                              type="month"
                              value={selectedMonth}
                              onChange={(e) => setSelectedMonth(e.target.value)}
                         />
                    </div>
                    <div className="filter-group col-auto">
                         <label>To Month</label>
                         <input
                              className='form-control form-control-sm'
                              type="month"
                              value={selectedToMonth}
                              onChange={(e) => setSelectedToMonth(e.target.value)}
                         />
                    </div>
                    <div className="filter-group col-auto" style={{ display: 'flex', alignItems: 'end' }}>
                         <button className="btn btn-primary btn-sm col-auto mt-3" onClick={handleFetchAttendance}>
                              Fetch Attendance
                         </button>
                    </div>
                    <div className="filter-group col-auto" style={{ display: 'flex', alignItems: 'end' }}>
                         <button className="btn btn-success btn-sm col-auto mt-3" onClick={handleDownloadExcel}>
                              <FaDownload /> Download Excel
                         </button>
                    </div>
               </div>



               {/* Summary Cards */}
               {days.length > 0 && employeeType === "DOCTOR" && (
                    <div className="summary-cards row">
                         <div className="col-auto row">
                              <b className='col-auto'>PRESENT DAYS</b>
                              <span className="col-auto">{shortfallSummary.totalPresentDays}</span>
                         </div>
                         <div className="col-auto row">
                              <b className='col-auto'>WORKED HOURS</b>
                              <span className="col-auto">{shortfallSummary.totalWorkedHours}</span>
                         </div>
                         <div className="col-auto row">
                              <b className='col-auto'>WEEKEND DAYS</b>
                              <span className="col-auto">{shortfallSummary.totalWeekendDays}</span>
                         </div>

                         <div className="col-auto row">
                              <b className='col-auto'>EXEMPTION LIMIT</b>
                              <span className="col-auto">{shortfallSummary.exemptionLimit}</span>
                         </div>
                    </div>
               )}

               {days.length > 0 && employeeType === "CONTRACTUAL_EMPLOYEE" && (
                    <div className="row summary-cards row">
                         <div className="col-auto row">
                              <b className='col-auto'>PRESENT DAYS</b>
                              <span className="col-auto">{shortfallSummary.totalPresentDays}</span>
                         </div>
                         <div className="col-auto row">
                              <b className='col-auto'>WORKED HOURS</b>
                              <span className="col-auto">{shortfallSummary.totalWorkedHours}</span>
                         </div>
                         <div className="col-auto row">
                              <b className='col-auto'>WEEKEND DAYS</b>
                              <span className="col-auto">{shortfallSummary.totalWeekendDays}</span>
                         </div>
                    </div>
               )}

               <div className="details-content">
                    {days.length > 0 ? (
                         isDoctorSplit ? (isMobile ? renderDoctorSplitMobileCards() : renderDoctorSplitDesktopTable()) : (isMobile ? renderMobileCards() : renderDesktopTable())
                    ) : (
                         <div className="no-data-message">
                              <p>{selectedMonth && selectedToMonth ? "No attendance records found for selected period" : "Please select date range to view attendance"}</p>
                         </div>
                    )}
               </div>

               {days.length > 0 && (
                    <div className="footer-summary">
                         Total Days: {days.length} | Present: {shortfallSummary.totalPresentDays} | Absent: {attendance.attendance?.absent_days || 0}
                    </div>
               )}
          </div>
     );
}

export default EmployeeAttendanceDetails;